import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation } from 'react-router-dom';

import { Show } from '../show';

import DesktopHeader from './desktop';
import MobileHeader from './mobile';

import { useScrollPosition } from '../../lib/hooks';

import NavigationBar from '../navigation-bar';
import StatusBarContainer from '../../containers/status-bar-container';
import { HOMEWORK_EXECUTION, RESULTS, PAUSE, START_PAGE } from '../../constants/routes';

import FluidContainer from '../layouts/components/fluid-container';
import Hidden from '../layouts/components/hidden';

import NotificationContainer from '../../containers/notification-container';
import { SOURCES } from '../../constants/sources';
import { printTime, redirectToSourceHome } from '../../lib/utils';
import ColumnContainer from '../layouts/components/column-container';
import { getCurrentSection, getSectionsTotalTime } from '../../lib/status';

export const getTimerTooltipText = (status, homeworkTime) => {
    if (!!status.sections) {
        if (status.started || status.resumed) {
            return `Tempo sezione ${printTime(getCurrentSection(status.sections).time)}`;
        }

        return `Tempo iniziale ${printTime(getSectionsTotalTime(status.sections))}`;
    }

    return `Tempo iniziale ${printTime(homeworkTime)}`;
};

const Bar = ({ applicationStatus, homeworkStatus }) => (
    <ColumnContainer width={[1, 1, 10 / 12]}>
        <ColumnContainer width={[1, 1, 10 / 12]} height={48} bg="surface-grey-200" boxShadow="shadow-01dp">
            <ColumnContainer width={[0, 0, 1 / 12]} />

            <ColumnContainer width={[1, 1, 8 / 12]} paddingX={['xs', 'tablet', 'desktop']}>
                {applicationStatus.completed && !homeworkStatus.error && <NavigationBar />}
            </ColumnContainer>

            <ColumnContainer width={[0, 0, 1 / 12]} />
        </ColumnContainer>
    </ColumnContainer>
);

const Header = ({
    applicationStatus,
    authors,
    book,
    homeworkStatus,
    homeworkTime,
    isLoading,
    numberOfExercises = 0,
    title,
    type,
    tick,
    terminateHomework,
    showModal,
    source,
}) => {
    const { y: scrollY } = useScrollPosition();

    const location = useLocation();

    let bottom = [-106, -106, -92];

    if (location.pathname === START_PAGE) {
        bottom = [-50, -20, -20];
    }

    return (
        <ColumnContainer position="fixed" top={0} zIndex={1}>
            <Hidden below="desktop">
                <DesktopHeader
                    applicationStatus={applicationStatus}
                    authors={authors}
                    book={book}
                    homeworkStatus={homeworkStatus}
                    homeworkTime={homeworkTime}
                    isLoading={isLoading}
                    numberOfExercises={numberOfExercises}
                    showModal={showModal}
                    terminateHomework={terminateHomework}
                    tick={tick}
                    type={type}
                    redirect={() => redirectToSourceHome(source)}
                />
            </Hidden>

            <Hidden above="desktop">
                <MobileHeader
                    applicationStatus={applicationStatus}
                    authors={authors}
                    book={book}
                    homeworkStatus={homeworkStatus}
                    homeworkTime={homeworkTime}
                    isLoading={isLoading}
                    numberOfExercises={numberOfExercises}
                    showModal={showModal}
                    terminateHomework={terminateHomework}
                    tick={tick}
                    title={title}
                    type={type}
                    redirect={() => redirectToSourceHome(source)}
                />
            </Hidden>

            <FluidContainer>
                <Hidden below="desktop">
                    <ColumnContainer width={[0, 0, 2 / 12]} backgroundColor="surface-grey-050" zIndex={4} height={52} />
                </Hidden>

                {numberOfExercises > 1 && (
                    <Routes>
                        <Route
                            path={HOMEWORK_EXECUTION}
                            element={<Bar applicationStatus={applicationStatus} homeworkStatus={homeworkStatus} />}
                        />
                        <Route path={RESULTS} element={<Bar applicationStatus={applicationStatus} homeworkStatus={homeworkStatus} />} />
                        <Route path={PAUSE} element={<Bar applicationStatus={applicationStatus} homeworkStatus={homeworkStatus} />} />
                    </Routes>
                )}
            </FluidContainer>

            <FluidContainer>
                <Hidden above="desktop">
                    <Show when={scrollY < 120 && !((!applicationStatus.completed && applicationStatus.error) || homeworkStatus.error)}>
                        <ColumnContainer width={[1, 1, 10 / 12]}>
                            <StatusBarContainer />
                        </ColumnContainer>
                    </Show>
                </Hidden>
            </FluidContainer>

            <NotificationContainer bottom={bottom} />
        </ColumnContainer>
    );
};

Header.propTypes = {
    applicationStatus: PropTypes.object,
    authors: PropTypes.string,
    book: PropTypes.string,
    homeworkTime: PropTypes.number,
    homeworkStatus: PropTypes.object,
    isLoading: PropTypes.bool,
    numberOfExercises: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.string,
    tick: PropTypes.func,
    terminateHomework: PropTypes.func,
    showModal: PropTypes.func,
    source: PropTypes.oneOf(Object.values(SOURCES)),
};

export default Header;
